import { FileText, PenLine, ClipboardList, UserRound } from "lucide-react";
import type { FC } from "react";

import { TableCell } from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

import type {
  FieldSource,
  FieldSourceKind
} from "../../../api/types/foreignNational";
import { formatDayLabel } from "../lib/records";

const KIND_LABEL: Record<FieldSourceKind, string> = {
  document: "Document",
  intake: "Intake",
  client_correction: "Client",
  manual: "Entered"
};

const KIND_ICON = {
  document: FileText,
  intake: ClipboardList,
  client_correction: UserRound,
  manual: PenLine
} as const;

const KIND_DESCRIPTION: Record<FieldSourceKind, string> = {
  document: "Read from a document",
  intake: "From the client's intake answers",
  client_correction: "Suggested by the client, then accepted",
  manual: "Entered by the team"
};

export const sourceLabel = (source?: FieldSource | null): string | null => {
  if (!source) return null;
  return source.label?.trim() || KIND_LABEL[source.kind] || null;
};

/**
 * Where a value came from ("Passport", "Intake", "Entered"), with when it
 * was accepted in a tooltip. Renders nothing without a source.
 */
export const SourceTag: FC<{
  source?: FieldSource | null;
  className?: string;
}> = ({ source, className }) => {
  const label = sourceLabel(source);
  if (!source || !label) return null;
  const kind = (
    source.kind in KIND_ICON ? source.kind : "manual"
  ) as FieldSourceKind;
  const Icon = KIND_ICON[kind];
  const when = source.accepted_at ? formatDayLabel(source.accepted_at) : null;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn(
            "inline-flex h-4 max-w-36 items-center gap-1 truncate rounded-xs bg-fill-2 px-1.5 text-xs font-medium text-fg-3",
            kind === "document" && "bg-accent-soft text-accent-seed",
            kind === "client_correction" && "bg-warn-soft text-warn",
            className
          )}
        >
          <Icon className="size-2.5 shrink-0" aria-hidden />
          <span className="truncate">{label}</span>
        </span>
      </TooltipTrigger>
      <TooltipContent side="top">
        {KIND_DESCRIPTION[kind]}
        {when && when !== "—" ? ` · ${when}` : ""}
      </TooltipContent>
    </Tooltip>
  );
};

/** History-table "Source" column. */
export const SourceCell: FC<{
  source?: FieldSource | null;
  className?: string;
}> = ({ source, className }) => (
  <TableCell className="h-12">
    <div className={cn("flex w-full items-center gap-1", className)}>
      {sourceLabel(source) ? (
        <SourceTag source={source} />
      ) : (
        <span className="text-sm text-fg-3">—</span>
      )}
    </div>
  </TableCell>
);
